import { type ComponentProps } from 'react';

import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { type DateRange } from 'react-day-picker';

import { cn, getUTCOffset } from '@/lib/utils';

/** Available date display formats */
export type DateFormat =
  | 'digit' // "01/01/2025"
  | 'digit-short' // "01/01/25"
  | 'full' // "January 1, 2025"
  | 'short' // "Jan 1, 2025"
  | 'datetime' // "January 1, 2025, 2:30 PM"
  | 'datetime-full' // "January 1, 2025, 2:30:45 PM"
  | 'datetime-at' // "January 1, 2025 at 14:30"
  | 'datetime-at-full' // "January 1, 2025 at 14:30:45"
  | 'datetime-utc-offset' // "January 1, 2025 at 14:30 +00:00"
  | 'iso' // "2025-01-01 14:30 (UTC +1)"
  | 'iso-short' // "2025-01-01 14:30"
  | 'relative'; // "2 hours ago", "in 3 days"

/** Single date input type */
type SingleDateInput = Date | number | string;

/** Date input - can be a single date or a date range */
type DateInput = SingleDateInput | DateRange;

// Format pattern mapping
const formatPatterns: Record<Exclude<DateFormat, 'relative'>, string> = {
  digit: 'dd/MM/yyyy',
  'digit-short': 'dd/MM/yy',
  full: 'PPP',
  short: 'PP',
  datetime: 'PPp',
  'datetime-full': 'PPpp',
  'datetime-at': "PPP 'at' HH:mm",
  'datetime-at-full': "PPP 'at' HH:mm:ss",
  'datetime-utc-offset': "PPP 'at' HH:mm xxx",
  iso: "yyyy-MM-dd HH:mm '(UTC'x')'",
  'iso-short': 'yyyy-MM-dd HH:mm',
};

// Check if input is a DateRange
function isDateRange(date: DateInput): date is DateRange {
  return (
    typeof date === 'object' &&
    !(date instanceof Date) &&
    ('from' in date || 'to' in date)
  );
}

// Normalize single date input
function normalizeDate(date: SingleDateInput): Date {
  if (date instanceof Date) return date;
  if (typeof date === 'number') return new Date(date);
  if (typeof date === 'string') return parseISO(date);
  return new Date(NaN);
}

/** Get browser timezone */
const getBrowserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export interface DateDisplayProps
  extends Omit<ComponentProps<'time'>, 'dateTime'> {
  /** The date to display - accepts Date, number (timestamp), ISO string, or DateRange */
  date: DateInput;
  /** Display format preset */
  displayFormat?: DateFormat;
  /** Custom format string (overrides displayFormat) */
  formatString?: string;
  /** Fallback text when date is invalid */
  fallback?: string;
  /** Include suffix for relative format (e.g., "ago" or "in X") */
  addSuffix?: boolean;
  /** Timezone to display the date in (defaults to browser timezone) */
  timezone?: string;
  /** Whether to show GMT offset suffix (e.g., "(UTC+1)") */
  showOffset?: boolean;
  /** Separator for date ranges (default: " → ") */
  rangeSeparator?: string;
}

export const DateDisplay = ({
  date,
  displayFormat = 'iso',
  formatString,
  fallback = '-',
  addSuffix = true,
  timezone,
  showOffset = false,
  rangeSeparator = ' → ',
  className,
  ...props
}: DateDisplayProps) => {
  // Use provided timezone or default to browser timezone
  const tz = timezone ?? getBrowserTimezone();
  // Helper to format a single date
  const formatSingleDate = (singleDate: Date): string => {
    const zonedDate = toZonedTime(singleDate, tz);
    return displayFormat === 'relative'
      ? formatDistanceToNow(singleDate, { addSuffix })
      : format(zonedDate, formatString ?? formatPatterns[displayFormat]);
  };

  // Handle DateRange
  if (isDateRange(date)) {
    const fromDate = date.from ? normalizeDate(date.from) : null;
    const toDate = date.to ? normalizeDate(date.to) : null;

    // If no valid dates in range, show fallback
    if ((!fromDate || !isValid(fromDate)) && (!toDate || !isValid(toDate))) {
      return <span className={className}>{fallback}</span>;
    }

    const offsetSuffix = showOffset
      ? ` (${getUTCOffset(tz, fromDate ?? toDate ?? new Date())})`
      : '';

    // Only from date
    if (fromDate && isValid(fromDate) && (!toDate || !isValid(toDate))) {
      return (
        <time
          dateTime={fromDate.toISOString()}
          className={cn(className)}
          {...props}
        >
          {formatSingleDate(fromDate)}
          {offsetSuffix}
        </time>
      );
    }

    // Both from and to dates
    if (fromDate && isValid(fromDate) && toDate && isValid(toDate)) {
      return (
        <span className={cn(className)} {...props}>
          <time dateTime={fromDate.toISOString()}>
            {formatSingleDate(fromDate)}
          </time>
          {rangeSeparator}
          <time dateTime={toDate.toISOString()}>
            {formatSingleDate(toDate)}
          </time>
          {offsetSuffix}
        </span>
      );
    }

    return <span className={className}>{fallback}</span>;
  }

  // Handle single date
  const normalizedDate = normalizeDate(date);
  if (!isValid(normalizedDate)) {
    return <span className={className}>{fallback}</span>;
  }

  const formattedDate = formatSingleDate(normalizedDate);
  const displayText = showOffset
    ? `${formattedDate} (${getUTCOffset(tz, normalizedDate)})`
    : formattedDate;

  return (
    <time
      dateTime={normalizedDate.toISOString()}
      className={cn(className)}
      {...props}
    >
      {displayText}
    </time>
  );
};
